import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, createGlobalStyle } from 'styled-components';
import FocusLock from 'react-focus-lock';
import { hideOthers } from 'aria-hidden';
import { DialogContentBase } from './DialogContentBase';
import { ModalContext } from './ModalContext';
import { OverlayBase } from './OverlayBase';
import { a11y } from './strings';
import { useModalObserver } from './useModalObserver';
import whisper from '../whisper';
const GlobalStyle = createGlobalStyle `
  ${({ open }) => `
    body, html {
      ${open ? 'overflow: hidden !important;' : ''}
    }
  `}
`;
const StyledFocusLock = styled(FocusLock)(({ theme: { media }, $fullScreenSmDown, $fullScreenMdOnly }) => css `
    ${media.down('sm')} {
      ${$fullScreenSmDown ? 'height: 100%' : ''}
    }
    ${media.between('md', 'md')} {
      ${$fullScreenMdOnly ? 'height: 100%' : ''}
    }
  `);
const usePortalWrapper = () => {
    const [portalWrapper, setPortalWrapper] = React.useState(null);
    React.useEffect(() => {
        const newPortalWrapper = document.createElement('div');
        document.body.appendChild(newPortalWrapper);
        setPortalWrapper(newPortalWrapper);
        return () => newPortalWrapper.remove();
    }, []);
    return portalWrapper;
};
export const ModalBase = ({ content, disableCloseOnOverlayClick = false, disableFullScreenSmDown, fullScreenMd, height, width, hideCloseButton, trigger, className, open, onOpenChange, ariaLabelCloseButton = a11y.ariaLabelCloseButton, allytmlnCloseButton, dataTrackRegion, disableReturnFocus, }) => {
    const [originallyFocusedElement, setOriginallyFocusedElement] = React.useState(null);
    const { dialogContentRef, isIntersectingViewport } = useModalObserver({
        isModalOpen: open,
    });
    const handleOverlayClick = React.useCallback(() => {
        if (!disableCloseOnOverlayClick) {
            onOpenChange(false);
        }
    }, [disableCloseOnOverlayClick, onOpenChange]);
    const [dialogRef, setDialogRef] = React.useState(null);
    useEffect(() => {
        if (open && !disableReturnFocus) {
            setOriginallyFocusedElement(document.activeElement);
        }
        if (!open && !disableReturnFocus && originallyFocusedElement) {
            ;
            originallyFocusedElement.focus();
            setOriginallyFocusedElement(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [disableReturnFocus, open]);
    useEffect(() => {
        let undo;
        if (open && dialogRef) {
            undo = hideOthers(dialogRef);
            const focusTarget = dialogRef === null || dialogRef === void 0 ? void 0 : dialogRef.querySelector('[tabindex="-1"]');
            if (focusTarget instanceof HTMLElement) {
                focusTarget.focus();
            }
            else {
                whisper.error({
                    message: `The Modal component was unable to find an element with tabIndex = -1 inside its contents. The contents must contain an element with tabIndex = -1 in order to receive focus when the Modal opens.`,
                });
            }
        }
        return () => {
            if (undo) {
                undo();
            }
        };
    }, [open, dialogRef]);
    useEffect(() => {
        return () => onOpenChange(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const portalWrapper = usePortalWrapper();
    return (React.createElement(ModalContext.Provider, { value: { open, setOpen: onOpenChange } },
        trigger,
        React.createElement(GlobalStyle, { open: open }),
        portalWrapper &&
            createPortal(React.createElement(OverlayBase, { "$fullScreenSmDown": !disableFullScreenSmDown, "$fullScreenMdOnly": !!fullScreenMd, "$isIntersectingViewport": isIntersectingViewport, open: open, onClick: handleOverlayClick },
                React.createElement(StyledFocusLock, { "$fullScreenSmDown": !disableFullScreenSmDown, "$fullScreenMdOnly": !!fullScreenMd, onActivation: (el) => setDialogRef(el), onDeactivation: () => setDialogRef(null), hasPositiveIndices: false, autoFocus: false },
                    React.createElement(DialogContentBase, { content: content, disableFullScreenSmDown: disableFullScreenSmDown, fullScreenMd: fullScreenMd, height: height, width: width, hideCloseButton: hideCloseButton, className: className, ariaLabelCloseButton: ariaLabelCloseButton, allytmlnCloseButton: allytmlnCloseButton, dataTrackRegion: dataTrackRegion, dialogContentRef: dialogContentRef, onClose: () => onOpenChange(false) }))), portalWrapper)));
};
//# sourceMappingURL=ModalBase.js.map